import React, { type MutableRefObject, useEffect, useRef } from 'react';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import type { WatchersConfig } from '@atlassian/jira-issue-create-watchers/src/services/types.tsx';
import WatchersApp from '@atlassian/jira-issue-create-watchers/src/ui/async.tsx';
import BaseTriggerButton from '@atlassian/jira-issue-create-watchers/src/ui/watchers-trigger-button/base-button/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import type { ControllerFull } from '@atlassian/jira-platform-services-user-current/src/types.tsx';

type Props = {
	configs?: WatchersConfig;
	hasWatchersInitialized?: MutableRefObject<boolean>;
	shouldRenderInDropdown?: boolean;
};

const Watchers = ({
	configs,
	shouldRenderInDropdown,
	hasWatchersInitialized: hasWatchersInitializedForNewModal,
}: Props) => {
	const hasWatchersInitializedForOldModal = useRef(false);

	/**
	 * For new modal, the modal can be minimized due to 3-stage-create, and if it is minimized the component
	 * is completely unmounted. un-minimizing the modal would lead to the default users being added again if
	 * they have remove themselves earlier. Which means keeping track of that only within this component is
	 * impossible, so that we have to declare the ref one level up which would not be unmounted on `modal-view-change`.
	 *
	 * For old modal tho, the `hasWatchersInitializedForNewModal` would be `undefined` because the old modal is rendered from
	 * a complete different place. And for old modal, having a ref within this component is more than enough.
	 */
	const hasWatchersInitialized =
		hasWatchersInitializedForNewModal || hasWatchersInitializedForOldModal;

	const { isAutoWatchEnabled, isWatchersEnabled } = configs || {};

	const {
		data: { user: currentUser },
		loading,
		error,
	} = useCurrentUser() as ControllerFull; // eslint-disable-line @typescript-eslint/consistent-type-assertions

	const [{ watchers, selectedProject }, { setWatchers }] = useFormContext();

	const shouldShowWatchers =
		selectedProject &&
		configs &&
		!currentUser.isAnonymous &&
		!loading &&
		!error &&
		// NOTE: JPD has no ability to manage watchers at the time of release, this is a workaround to prevent watchers being added for JPD.
		selectedProject.projectType !== PRODUCT_DISCOVERY_PROJECT;

	const isWatching = watchers.some((watcher) => watcher.accountId === currentUser.accountId);

	/**
	 * The current user is the default user in the list because isAutoWatchEnabled is turned on
	 * so the current user would be added to the list on init.
	 */
	useEffect(() => {
		if (
			isAutoWatchEnabled &&
			shouldShowWatchers &&
			currentUser &&
			!hasWatchersInitialized.current &&
			!isWatching
		) {
			setWatchers([...watchers, currentUser]);
			hasWatchersInitialized.current = true;
		}
	}, [
		currentUser,
		shouldShowWatchers,
		isAutoWatchEnabled,
		setWatchers,
		watchers,
		isWatching,
		hasWatchersInitialized,
	]);

	if (!shouldShowWatchers || (!isWatchersEnabled && shouldRenderInDropdown)) return null;

	if (!isWatchersEnabled) return <BaseTriggerButton isDisabled />;

	return (
		<ShortcutScope>
			<WatchersApp
				watchers={watchers}
				projectKey={selectedProject.projectKey}
				setWatchers={setWatchers}
				isWatching={isWatching}
				currentUser={currentUser}
				shouldRenderInDropdown={shouldRenderInDropdown}
				{...configs}
			/>
		</ShortcutScope>
	);
};

export default Watchers;
